import type { UpdatePasswordData, UpdateProfileData } from '@/api/resources/auth'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { meQueryOptions, updatePassword, updateProfile } from '@/api/resources/auth'
import { ChangePasswordForm } from '@/components/account/change-password-form'
import { ProfileForm } from '@/components/account/profile-form'
import { PageHeader } from '@/components/layout/page-header'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'
import { useAuthStore } from '@/stores/auth-store'

export const Route = createFileRoute('/_app/account')({
  head: () => ({ meta: [{ title: 'Account - MyJunea' }] }),
  component: AccountPage,
})

function AccountPage(): React.ReactElement {
  const queryClient = useQueryClient()
  const { data: user } = useSuspenseQuery(meQueryOptions())

  const profileErrors = useServerErrors()
  const passwordErrors = useServerErrors()

  const profileMutation = useMutation({
    mutationFn: async (values: UpdateProfileData) => updateProfile(values),
    onSuccess: (updatedUser) => {
      profileErrors.clearServerErrors()
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me })
      useAuthStore.getState().setUser(updatedUser)
      toast.success('Profile updated')
    },
    onError: profileErrors.handleMutationError,
  })

  const passwordMutation = useMutation({
    mutationFn: async (values: UpdatePasswordData) => updatePassword(values),
    onSuccess: () => {
      passwordErrors.clearServerErrors()
      toast.success('Password updated')
    },
    onError: passwordErrors.handleMutationError,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Account settings"
        description="Manage your personal information and preferences."
      />
      <div className="grid gap-6 md:grid-cols-2">
        <ProfileForm
          defaultValues={{
            first_name: user.first_name ?? '',
            last_name: user.last_name ?? '',
            email: user.email,
          }}
          onSubmit={(values) => profileMutation.mutate(values)}
          isPending={profileMutation.isPending}
          serverErrors={profileErrors.serverErrors}
        />
        <ChangePasswordForm
          onSubmit={(values) => passwordMutation.mutate(values)}
          isPending={passwordMutation.isPending}
          serverErrors={passwordErrors.serverErrors}
        />
      </div>
    </div>
  )
}
